import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import type { ToolRef, ToolRefAction } from '@crowdex/plugin-contracts';
import { useMemo } from 'react';
import { SummaryButtonRow, SummarySection } from '@/components/desktop-summary/summary-primitives';
import { ItemIcon } from '@/components/item-icon';
import { artifactGlyph } from '@/components/library-page/library-card';
import { refApp } from '@/features/apps/ref-targets';
import { isRenderOf } from '@/features/apps/renders';
import { useArtifactIndex } from '@/features/apps/use-artifact-index';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { artifactOfRef, refActionLabel } from '@/lib/conversations/tool-refs';
import { useDesktopPreviewState } from '@/lib/desktop-preview-state';

/** The core's own types: plain files, which "files changed" and Review list, not this section. */
const CORE_PLUGIN = 'core';

/**
 * What a conversation made (its artifacts' provenance, the same as their "From:" link): every
 * artifact of an app's type it created or changed, an account one too (an avatar), the newest
 * first. Plain files stay with "files changed" and Review.
 */
export function conversationArtifacts(
  artifacts: readonly ArtifactListingEntry[],
  conversation: string,
): ArtifactListingEntry[] {
  const seen = new Set<string>();
  return artifacts
    .filter(
      (artifact) =>
        artifact.conversation === conversation &&
        artifact.plugin !== CORE_PLUGIN &&
        !seen.has(artifact.id) &&
        seen.add(artifact.id) !== undefined,
    )
    .toSorted((left, right) => Date.parse(right.modifiedAt) - Date.parse(left.modifiedAt));
}

/** The artifacts a conversation made, from the index (listed again as its messages change). */
export function useMadeArtifacts(
  conversation: string | null,
  refreshKey?: unknown,
): ArtifactListingEntry[] {
  return useConversationArtifactIndex(conversation, refreshKey).made;
}

/**
 * The artifacts a conversation can see (`index`, where its tools' refs find theirs) and those it
 * made (`made`), read again as its messages change.
 */
export function useConversationArtifactIndex(
  conversation: string | null,
  refreshKey?: unknown,
): { made: ArtifactListingEntry[]; index: readonly ArtifactListingEntry[] } {
  const index = useArtifactIndex(conversation, { refreshKey });
  // A preview's board lists its own conversation's artifacts.
  const board = useDesktopPreviewState((state) => state.artifacts !== null);
  const made = useMemo(
    () =>
      conversation === null
        ? []
        : conversationArtifacts(
            board
              ? index.artifacts.map((artifact) => ({
                  ...artifact,
                  conversation: artifact.conversation ?? conversation,
                }))
              : index.artifacts,
            conversation,
          ),
    [board, index.artifacts, conversation],
  );
  return { made, index: index.artifacts };
}

/** A row of the Artifacts section: an indexed artifact, or one a tool named that the index lacks. */
export type SummaryArtifactRow = {
  key: string;
  title: string;
  /** Made, Changed or Read. */
  action: ToolRefAction | undefined;
  artifact: ArtifactListingEntry | undefined;
  ref: ToolRef | undefined;
  /** A render, under the source it was made from. */
  derived: boolean;
};

/**
 * What the conversation made, changed or read of artifacts: those its provenance names (made
 * here) and those its tools' refs name (read, changed), each once, a render under its source.
 */
export function summaryArtifactRows(
  made: readonly ArtifactListingEntry[],
  refs: readonly ToolRef[],
  index: readonly ArtifactListingEntry[] = made,
): SummaryArtifactRow[] {
  const rows = new Map<string, SummaryArtifactRow>();
  for (const artifact of made)
    rows.set(artifact.id, {
      key: artifact.id,
      title: artifact.title,
      action: 'made',
      artifact,
      ref: undefined,
      derived: false,
    });
  for (const ref of refs) {
    if (ref.kind !== 'artifact') continue;
    const artifact = artifactOfRef(ref, index) ?? artifactOfRef(ref, made);
    const key = artifact?.id ?? `ref:${ref.app}:${ref.id}`;
    const known = rows.get(key);
    rows.set(key, {
      key,
      title: artifact?.title ?? ref.title,
      // What it made here outweighs what a later step only read.
      action: known?.action === 'made' ? 'made' : (ref.action ?? known?.action),
      artifact,
      ref,
      derived: ref.derivedFrom !== undefined,
    });
  }
  // A render follows its source when the source is listed: the index's link (`derivedFrom`,
  // `isRenderOf`), or the source a ref names.
  const list = [...rows.values()];
  const renderOf = (render: SummaryArtifactRow, source: SummaryArtifactRow) =>
    render !== source &&
    ((render.ref?.derivedFrom !== undefined && render.ref.derivedFrom === source.key) ||
      (render.artifact !== undefined &&
        source.artifact !== undefined &&
        isRenderOf(render.artifact, source.artifact)));
  const ordered: SummaryArtifactRow[] = [];
  for (const row of list) {
    if (list.some((source) => renderOf(row, source))) continue;
    ordered.push({ ...row, derived: false });
    for (const render of list)
      if (renderOf(render, row)) ordered.push({ ...render, derived: true });
  }
  return ordered;
}

/**
 * The summary's "Artifacts · N" (D5, mobile 36): what the conversation made, changed or read,
 * each with its type's glyph, its title, what it did and its editor; a render under its source.
 * One opens in the conversation's side panel, on its item. None: no section.
 */
export function SummaryArtifactsSection({
  artifacts,
  refs = [],
  index,
  onOpen,
  onOpenRef,
}: {
  artifacts: readonly ArtifactListingEntry[];
  /** What the conversation's tools touched: their artifacts are listed too. */
  refs?: readonly ToolRef[];
  /** The artifacts the conversation can see, where a ref finds its own. */
  index?: readonly ArtifactListingEntry[];
  onOpen: (artifact: ArtifactListingEntry) => void;
  onOpenRef?: (ref: ToolRef) => void;
}) {
  const workbench = useWorkbench();
  const rows = summaryArtifactRows(artifacts, refs, index);
  if (rows.length === 0) return null;
  return (
    <SummarySection
      count={rows.length}
      rows={rows.map((row) => {
        const glyph =
          row.artifact !== undefined
            ? artifactGlyph(row.artifact, workbench)
            : row.ref !== undefined
              ? artifactGlyph({ plugin: row.ref.app, type: row.ref.type ?? '' }, workbench)
              : null;
        const editor =
          row.artifact?.pluginTitle ||
          (row.ref === undefined ? '' : refApp(row.ref.app, workbench).title);
        const action = refActionLabel(row.action);
        const open =
          row.artifact !== undefined
            ? () => onOpen(row.artifact as ArtifactListingEntry)
            : row.ref !== undefined && onOpenRef !== undefined
              ? () => onOpenRef(row.ref as ToolRef)
              : undefined;
        return (
          <div className={row.derived ? 'pl-5' : undefined} key={row.key}>
            <SummaryButtonRow
              icon={
                glyph === null ? null : (
                  <ItemIcon className="size-4" icon={glyph.icon} src={glyph.src} />
                )
              }
              onClick={open}
              title={[row.artifact?.typeTitle, row.title].filter(Boolean).join(' · ')}
              trailing={
                <>
                  {action !== null && <span>{action}</span>}
                  {action !== null && editor !== '' && ' · '}
                  {editor !== '' && <span>{editor}</span>}
                </>
              }
            >
              {row.title}
            </SummaryButtonRow>
          </div>
        );
      })}
      title="Artifacts"
    />
  );
}
